import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import {
    Star,
    Quotes,
    Sparkle,
    Check,
    Buildings,
    PencilSimple,
} from "@phosphor-icons/react";

const STATUS_META = {
    pending: { label: "Pending review", cls: "bg-[#FEF3C7] text-[#B45309]" },
    approved: { label: "Live", cls: "bg-[#ECFDF5] text-[#059669]" },
    hidden: { label: "Hidden", cls: "bg-[#F5F5F5] text-[#525252]" },
};

/**
 * Candidate testimonials page — shows any pending/approved/hidden testimonials
 * this candidate has submitted plus a prompt card for every client where the
 * candidate got Hired but hasn't yet left a review.
 */
export default function CandidateTestimonials() {
    const [data, setData] = useState(null);
    const [target, setTarget] = useState(null); // { client, job_title, job_id }
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        api
            .get("/me/candidate/testimonials")
            .then((r) => setData(r.data))
            .catch((e) => logError("Testimonials load failed:", e))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading || !data) return <div className="p-10 text-[#525252]">Loading…</div>;

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1000px] mx-auto" data-testid="candidate-testimonials-page">
            <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                    Share your story
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Testimonials.
                </h1>
                <p className="text-[#525252] mt-3 max-w-xl">
                    Recommend the agencies who placed you. Approved testimonials show up
                    on their public career pages and help other candidates decide.
                </p>
            </div>

            {/* Prompts — hired clients without a review */}
            {data.prompts.length > 0 && (
                <div className="mb-8" data-testid="testimonial-prompts">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                        Waiting on your review
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3">
                        {data.prompts.map((p) => (
                            <button
                                key={p.client.id}
                                onClick={() => setTarget(p)}
                                data-testid={`testimonial-prompt-${p.client.id}`}
                                className="bg-white border border-[#E5E5E5] hover:border-ink p-5 text-left transition-colors group"
                            >
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1 inline-flex items-center gap-1.5">
                                    <Sparkle size={11} weight="bold" /> Placed at
                                </p>
                                <p className="font-heading text-xl font-bold tracking-tight text-ink group-hover:text-cobalt transition-colors">
                                    {p.client.name}
                                </p>
                                <p className="text-xs text-[#525252] mb-3">as {p.job_title}</p>
                                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-cobalt">
                                    <PencilSimple size={11} weight="bold" /> Write a testimonial →
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Submitted testimonials */}
            {data.submitted.length > 0 ? (
                <div data-testid="testimonial-submitted-list">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                        Your testimonials
                    </p>
                    <div className="space-y-3">
                        {data.submitted.map((t) => {
                            const meta = STATUS_META[t.status] || STATUS_META.pending;
                            return (
                                <div
                                    key={t.id}
                                    data-testid={`testimonial-mine-${t.id}`}
                                    className="bg-white border border-[#E5E5E5] p-5 relative"
                                >
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1 inline-flex items-center gap-1.5">
                                                <Buildings size={11} weight="bold" /> {t.client_name}
                                            </p>
                                            <div className="flex gap-0.5">
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <Star
                                                        key={n}
                                                        size={13}
                                                        weight="fill"
                                                        className={n <= t.rating ? "text-[#F59E0B]" : "text-[#E5E5E5]"}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 shrink-0 ${meta.cls}`}>
                                            {meta.label}
                                        </span>
                                    </div>
                                    <p className="text-sm text-[#262626] leading-relaxed">
                                        &ldquo;{t.text}&rdquo;
                                    </p>
                                    <button
                                        onClick={() =>
                                            setTarget({
                                                client: { id: t.client_id, name: t.client_name },
                                                job_title: t.candidate_title,
                                                job_id: t.job_id,
                                                existing: t,
                                            })
                                        }
                                        data-testid={`testimonial-edit-${t.id}`}
                                        className="text-[11px] font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark mt-3 inline-flex items-center gap-1"
                                    >
                                        <PencilSimple size={11} weight="bold" /> Edit
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : data.prompts.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] p-10 text-center">
                    <Quotes size={28} weight="fill" className="text-[#E5E5E5] mx-auto mb-3" />
                    <p className="text-sm text-[#525252]">
                        Once you get hired through an agency on NaukriDaddy, you can
                        recommend them here.
                    </p>
                </div>
            ) : null}

            {target && (
                <TestimonialForm
                    target={target}
                    onClose={() => setTarget(null)}
                    onSaved={() => { setTarget(null); load(); }}
                />
            )}
        </div>
    );
}

function TestimonialForm({ target, onClose, onSaved }) {
    const existing = target.existing;
    const [rating, setRating] = useState(existing?.rating ?? 5);
    const [text, setText] = useState(existing?.text ?? "");
    const [anonymous, setAnonymous] = useState(existing?.anonymous ?? false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (text.trim().length < 40) {
            toast.error("Testimonials need at least 40 characters.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/me/candidate/testimonials", {
                client_id: target.client.id,
                job_id: target.job_id,
                candidate_title: target.job_title || "",
                rating,
                text: text.trim(),
                anonymous,
            });
            toast.success("Thanks! Your testimonial is pending review.");
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="testimonial-form-modal"
        >
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg bg-white shadow-2xl"
            >
                <div className="px-5 py-4 border-b border-[#F5F5F5]">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3]">
                        Recommend
                    </p>
                    <p className="font-heading text-xl font-bold tracking-tight text-ink">
                        {target.client.name}
                    </p>
                    {target.job_title && (
                        <p className="text-xs text-[#525252]">as {target.job_title}</p>
                    )}
                </div>
                <div className="px-5 py-5 space-y-4">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-2 block">
                            Rating
                        </label>
                        <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setRating(n)}
                                    data-testid={`testimonial-star-${n}`}
                                    aria-label={`${n} stars`}
                                    className="p-1"
                                >
                                    <Star
                                        size={26}
                                        weight="fill"
                                        className={n <= rating ? "text-[#F59E0B]" : "text-[#E5E5E5]"}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-2 block">
                            Your testimonial ({text.length}/800)
                        </label>
                        <textarea
                            data-testid="testimonial-text-input"
                            value={text}
                            onChange={(e) => setText(e.target.value.slice(0, 800))}
                            rows={5}
                            placeholder="Share what stood out about your experience — communication, speed, prep support, culture fit…"
                            required
                            minLength={40}
                            className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                        />
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={anonymous}
                            onChange={(e) => setAnonymous(e.target.checked)}
                            data-testid="testimonial-anonymous"
                            className="mt-0.5 accent-cobalt"
                        />
                        <span className="text-xs text-[#525252]">
                            Show as <b>anonymous</b> on the public career page (recruiters
                            still see your name for verification).
                        </span>
                    </label>
                </div>
                <div className="px-5 py-4 border-t border-[#F5F5F5] flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="testimonial-cancel"
                        className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-ink"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="testimonial-submit"
                        className="btn-gradient px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        <Check size={12} weight="bold" /> Submit
                    </button>
                </div>
            </form>
        </div>
    );
}
